<template>
  <div class="cd-page">

    <!-- 顶部导航 -->
    <!-- 顶部导航：进入任意单据页后隐藏，由 cd-tabbar 承担标题与返回 -->
    <van-nav-bar
      v-if="!billTab"
      class="cd-nav"
      fixed
      placeholder
      :title="contract.name ? contract.name : '合同详情'"
      left-text="返回"
      left-arrow
      @click-left="onNavLeft"
    />

    <!-- 【P24-1】总览区下拉刷新：覆盖 Hero + 合同信息 + 业务单据宫格（原先只有单据页可下拉） -->
    <van-pull-refresh v-if="!billTab" v-model="overviewRefreshing" @refresh="onOverviewRefresh">
    <!-- 合同头部 Hero（蓝底：销售金额单独放大 + 四个金额指标） -->
    <div class="cd-hero" v-if="loaded && !billTab">
      <div class="cd-hero__main">
        <div class="cd-hero__main-label">销售金额（实际销售金额）</div>
        <div class="cd-hero__main-value">¥{{ fmtMoney(contract.sales_amount) }}</div>
      </div>
      <div class="cd-hero__metrics">
        <div class="cd-metric">
          <div class="cd-metric__label">订单总金额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.total_amount) }}</div>
        </div>
        <div class="cd-metric">
          <div class="cd-metric__label">支付金额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.payment_amount) }}</div>
        </div>
        <div class="cd-metric">
          <div class="cd-metric__label">待支付金额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.pending_payment_amount) }}</div>
        </div>
        <div class="cd-metric">
          <div class="cd-metric__label">当前余额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.balance_amount) }}</div>
        </div>
      </div>
    </div>

    <van-loading v-if="!loaded && !billTab" class="cd-loading" type="spinner" color="#2b5fd9" />

    <!-- 合同概要：名称 / 编号 / 状态 / 类型 -->
    <div class="cd-info" v-if="loaded && !billTab">
      <div class="cd-info__grid">
        <div class="cd-info__item cd-info__item--full">
          <span class="cd-info__label">合同名称</span>
          <span class="cd-info__value">{{ contract.name || '—' }}</span>
        </div>
        <div class="cd-info__item">
          <span class="cd-info__label">合同编号</span>
          <span class="cd-info__value">{{ contract.serial_number || '—' }}</span>
        </div>
        <div class="cd-info__item">
          <span class="cd-info__label">状态</span>
          <span class="cd-info__value">
            <van-tag :type="statusTagType" plain>{{ contract.status_name || '—' }}</van-tag>
          </span>
        </div>
        <div class="cd-info__item">
          <span class="cd-info__label">类型</span>
          <span class="cd-info__value">{{ contract.type_name || '—' }}</span>
        </div>
        <div class="cd-info__item cd-info__item--full">
          <span class="cd-info__label">所属客户</span>
          <span class="cd-info__value">{{ contract.customer_name || '—' }}</span>
        </div>
      </div>
      <van-button v-if="!mkReadonly" class="cd-edit-btn" size="small" plain icon="edit" @click="onEditContractClick">编辑合同</van-button>
    </div>

    <!-- 进度情况：stage 1待执行 / 2已预定 / 3已支付 / 4已全款 -->
    <div class="cd-panel" v-if="loaded && !billTab">
      <div class="cd-panel__hd">
        <span class="cd-sec__title">进度情况</span>
        <span class="cd-progress__pct">{{ stageText }} · {{ stagePct }}%</span>
      </div>
      <div class="cd-progress__bar">
        <div class="cd-progress__fill" :style="{ width: stagePct + '%' }"></div>
      </div>
      <div class="cd-steps">
        <div
          class="cd-step"
          v-for="s in stages"
          :key="s.value"
          :class="{ 'cd-step--on': stageIdx >= s.value, 'cd-step--cur': stageIdx === s.value }"
        >
          <div class="cd-step__dot">
            <van-icon v-if="stageIdx > s.value" name="success" />
            <span v-else>{{ s.value }}</span>
          </div>
          <div class="cd-step__label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <!-- 合同信息：对齐后台详情配置（业务信息 / 会议信息 / 系统信息），三组默认收起 -->
    <template v-if="loaded && !billTab">
      <div class="cd-panel" v-for="g in fieldGroups" :key="g.key">
        <div class="cd-fold" @click="toggleGroup(g.key)">
          <span class="cd-fold__title">{{ g.title }}</span>
          <span class="cd-fold__hint">{{ groupOpen[g.key] ? '收起' : '展开' }}</span>
          <van-icon :name="groupOpen[g.key] ? 'arrow-up' : 'arrow-down'" class="cd-fold__arrow" />
        </div>

        <div v-show="groupOpen[g.key]" class="cd-fold__body">
          <!-- 金额类字段：两列宫格 -->
          <div class="cd-mgrid" v-if="g.money.length">
            <!-- 【P36-fix】money 允许「同名不同口径」重复展示（如 total_deposits 出现两次），
                 :key 不能用 f.prop（会触发 Vue Duplicate keys 警告），改用 组key+prop+序号 保证唯一 -->
            <div class="cd-mgrid__cell" v-for="(f, mi) in g.money" :key="'m-' + g.key + '-' + f.prop + '-' + mi">
              <div class="cd-mgrid__label">{{ f.label }}</div>
              <div class="cd-mgrid__value">{{ moneyText(f.prop) }}</div>
            </div>
          </div>
          <!-- 文本类字段：整行 -->
          <div class="cd-info-row" v-for="(f, ri) in g.rows" :key="'r-' + g.key + '-' + f.prop + '-' + ri">
            <span class="cd-info-row__label">{{ f.label }}</span>
            <span class="cd-info-row__value">{{ f.strip ? plainText(contract[f.prop]) : textOf(f.prop) }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- 业务单据：小图标宫格，点击进入独立页面 -->
    <div class="cd-panel" v-if="loaded && !billTab">
      <div class="cd-panel__hd">
        <span class="cd-sec__title">业务单据</span>
        <span class="cd-panel__hint">点击进入</span>
      </div>
      <div class="cd-grid">
        <div class="cd-grid__item" v-for="tab in tabConfigs" :key="tab.key" @click="openBill(tab)">
          <div class="cd-grid__icon">
            <van-icon :name="tab.icon" />
            <!-- 04 数量改为标题括号展示；红色气泡仅在存在「驳回类」条目时显示（驳回/未通过/拒绝计数） -->
            <span class="cd-grid__badge" v-if="tabRejectCount(tab)">{{ tabRejectCount(tab) }}</span>
          </div>
          <div class="cd-grid__label">{{ tab.short || tab.title }}{{ (lists[tab.key]||[]).length ? ' (' + (lists[tab.key]||[]).length + ')' : '' }}</div>
        </div>
      </div>
    </div>
    </van-pull-refresh>

    <!-- 单据独立页面：点击宫格图标后覆盖展示，返回不跳路由 -->
    <div class="cd-billpage" v-if="billTab">
      <div class="cd-tabbar">
        <div class="cd-tabbar__top">
          <div class="cd-tabbar__left" @click="billFormMode ? closeForm() : closeBill()">
            <van-icon name="arrow-left" class="cd-tabbar__back" />
            <!-- 表单已改为底部弹窗（自带标题），这里始终显示单据名，避免标题重复 -->
            <span class="cd-tabbar__title">{{ billTab ? billTab.title : '' }}</span>
          </div>
          <template v-if="!billFormMode">
            <div class="cd-tabbar__search">
              <van-icon name="search" class="cd-tabbar__search-icon" />
              <input v-model="billSearch" type="text" placeholder="搜索" />
              <van-icon v-if="billSearch" name="clear" class="cd-tabbar__search-clear" @click.stop="billSearch=''" />
            </div>
            <div class="cd-tabbar__actions">
              <div class="cd-tabbar__btn" :class="{ 'cd-tabbar__btn--active': billFilterActive }" @click="openSortSheet">
                <van-icon name="filter-o" />
                <span>筛选</span>
              </div>
              <!-- 只读单据（审批 / 余额明细）：不显示「新增」 -->
              <div class="cd-tabbar__btn cd-tabbar__btn--primary" v-if="!mkReadonly && !billTab.readonly" @click="openForm('add')">
                <van-icon name="plus" />
                <span>新增</span>
              </div>
            </div>
          </template>
        </div>
        <div class="cd-tabbar__summary" v-if="!billFormMode">
          <span>共 {{ filteredBillTotal.count }} 条</span>
          <span v-if="filteredBillTotal.hasSum">合计 {{ billMoney(filteredBillTotal.sum) }}</span>
        </div>
      </div>

      <!-- ============ 列表页：与主列表（客户/商机/合同）完全同一套卡片样式 ============ -->
      <!-- 表单已改为底部弹窗，列表常驻作为背景（不再随 billFormMode 隐藏） -->
      <template>
        <!-- 下拉刷新 -->
        <van-pull-refresh v-model="billRefreshing" @refresh="onBillRefresh">
          <!-- 空态 -->
          <div class="bl-empty" v-if="!filteredBillRows.length">
            <div class="bl-empty__icon">
              <van-icon :name="billTab.icon" />
            </div>
            <div class="bl-empty__text">暂无{{ billTab.title }}</div>
            <div class="bl-empty__hint" v-if="!billTab.readonly">点击右上角「新增」创建</div>
            <div class="bl-empty__hint" v-else>{{ billTab.emptyHint || '仅可查看' }}</div>
          </div>

          <!-- 卡片列表：crm-item + customer-card + status-tag + btn-action -->
          <div class="list-box" v-else>
            <div class="crm-item" v-for="(item,i) in filteredBillRows" :key="item.row.sn || i">
              <div class="item-head customer-card" @click="openRowDetail(item.row)">
                <div class="customer-card__main">
                  <div class="customer-card__content">
                    <div class="customer-card__header">
                      <div class="customer-card__name-wrap">
                        <span class="customer-card__name">{{ item.title }}</span>
                        <span class="customer-card__role" v-if="item.amountText">{{ item.amountText }}</span>
                      </div>
                      <span class="status-tag" :class="item.statusClass" v-if="item.status">{{ item.status }}</span>
                    </div>
                    <div class="customer-card__row">{{ item.row1 }}</div>
                    <div class="customer-card__row customer-card__row--muted">{{ item.row2 }}</div>
                  </div>
                </div>
                <!-- 只读单据（审批 / 余额明细）：不显示编辑与删除，仅可点卡片查看详情 -->
                <div class="customer-card__actions" v-if="!mkReadonly && !billTab.readonly">
                  <div class="btn-action btn-action--primary" v-if="billTab.editFn && rowEditable(item.row)" @click.stop="openForm('edit', item.row)">✎ 编辑</div>
                  <div class="btn-action btn-action--primary" v-if="canSubmitRow(item.row)" @click.stop="submitRow(item.row)">⇪ 提交</div>
                  <div class="btn-action btn-action--danger" v-if="billTab.deleteFn && rowDeletable(item.row)" @click.stop="deleteRoomOrder(item.row)">🗑 删除</div>
                </div>
              </div>
            </div>
          </div>
          </van-pull-refresh>

          <!-- 单据详情（只读）：点卡片弹出，容器与表单同款；编辑走底部按钮 -->
          <van-popup v-model="detailShow" round position="bottom" :style="{ height: '88%' }">
            <div class="form-page">
              <div class="form-title">{{ billTab ? billTab.title : '' }}详情</div>
              <div class="form-body">
                <div class="ro-group ro-group--card" v-for="(g,gi) in detailGroups" :key="gi">
                  <div class="ro-group__hd">{{ g.name }}</div>
                  <div class="ro-field" v-for="(f,fi) in g.fields" :key="fi">
                    <span class="ro-label">{{ f.label }}</span>
                    <span class="ro-val ro-val--right">{{ f.money ? billMoney(f.value) : f.value }}</span>
                  </div>
                </div>
                <!-- 【P16】财务合同详情：底部关联区块（会议室/回款单/发票/退款记录） -->
                <div class="ro-group ro-group--card" v-for="(g,gi) in detailRelated" :key="'rel'+gi">
                  <div class="ro-group__hd">{{ g.label }}（{{ g.items.length }}）</div>
                  <div class="ro-field" v-for="(it,ii) in g.items" :key="ii">
                    <span class="ro-label">{{ it.text }}</span>
                    <span class="ro-val ro-val--right">{{ it.amount != null && it.amount !== '' ? billMoney(it.amount) : '' }}{{ it.status ? ' · ' + it.status : '' }}</span>
                  </div>
                  <div class="ro-empty" v-if="!g.items.length">暂无{{ g.label }}</div>
                </div>
                <div class="ro-empty" v-if="!detailGroups.length && !detailRelated.length">该单据暂无可展示字段</div>
              </div>
              <div class="form-footer">
                <van-button block plain @click="detailShow=false">关闭</van-button>
                <van-button block type="info" v-if="!mkReadonly && billTab && billTab.editFn && !billTab.readonly && rowEditable(detailRow)" @click="onDetailEdit">编辑</van-button>
              </div>
            </div>
          </van-popup>
      </template>

      <!-- ============ 底部弹窗表单：新增 / 编辑（对齐客户表单 form-page 容器） ============ -->
      <van-popup
        :value="!!billFormMode"
        @click-overlay="closeForm"
        round
        position="bottom"
        :style="{ height: '88%' }"
      >
        <div class="cd-formpage">
          <div class="form-title">{{ billFormMode==='edit' ? '编辑'+(billTab?billTab.title:'') : '添加'+(billTab?billTab.title:'') }}</div>
          <div class="cd-formpage__body">
          <div class="ro-group" v-for="g in addGroups" :key="g.name">
            <div class="ro-group__hd">{{ g.name }}</div>

            <template v-for="f in g.fields">
              <!-- 【P9】showIf 联动显隐：字段声明 showIf(form) 时按当前表单值判断 -->
              <template v-if="fieldVisible(f)">
              <!-- 日期：只读单元格 + 日期选择器 -->
              <div class="ro-field" v-if="f.type==='date'" :key="f.prop" @click="openDatePick(f.prop)">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val" :class="{'ro-val--ph': !addForm[f.prop]}">{{ addForm[f.prop] || '请选择' }}</span>
                <span class="ro-arrow">›</span>
              </div>

              <!-- 下拉选择：只读单元格 + ActionSheet；带 readonly 的字段（如「合同」）不可点选 -->
              <div class="ro-field" v-else-if="f.type==='select' && f.readonly" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val ro-val--right">{{ selectText(f) || '—' }}</span>
                <span class="ro-lock"><van-icon name="lock" /></span>
              </div>
              <div class="ro-field" v-else-if="f.type==='select'" :key="f.prop" @click="openSheet(f)">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val" :class="{'ro-val--ph': !selectText(f)}">{{ selectText(f) || (f.placeholder || '请选择') }}</span>
                <span class="ro-arrow">›</span>
              </div>

              <!-- 【P9】多选块（关联会议室订单）：chips 风格多选 + 全选；提交逗号分隔 sn -->
              <div class="ro-block" v-else-if="f.type==='multiselect'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <div class="ro-hint" v-if="!(f.options||[]).length">{{ f.placeholder || '加载选项中…' }}</div>
                <div class="ro-chips">
                  <span
                    class="ro-chip ro-chip--all"
                    :class="{'ro-chip--on': isMultiAllOn(f)}"
                    @click="toggleMultiAll(f)"
                  >全选</span>
                  <span
                    v-for="o in (f.options||[])"
                    :key="o.value"
                    class="ro-chip"
                    :class="{'ro-chip--on': isChipOn(addForm[f.prop], o.value)}"
                    @click="toggleMulti(f, o.value)"
                  >{{ o.label }}</span>
                </div>
                <div class="ro-hint" v-if="(addForm[f.prop]||[]).length">已选 {{ (addForm[f.prop]||[]).length }} 项</div>
              </div>

              <!-- 折扣率：只读，自动计算（销售价格 / 市场价格 × 10） -->
              <div class="ro-field" v-else-if="f.type==='discount'" :key="f.prop">
                <span class="ro-label">{{ f.label }}</span>
                <span class="ro-val" :class="{'ro-val--ph': !discountText}">
                  {{ discountText ? (discountText + ' 折') : (f.placeholder || '自动计算') }}
                </span>
              </div>

              <!-- 回款计划：日期 + 金额 多行 -->
              <div class="ro-block" v-else-if="f.type==='plans'" :key="f.prop">
                <span class="ro-label" v-if="f.label !== g.name">
                  <i v-if="isPlanRequired(f)" class="ro-req">*</i>{{ f.label }}
                </span>
                <div class="ro-hint" v-if="f.hint">{{ f.hint }}</div>
                <div class="ro-plans">
                  <div class="ro-plan" v-for="(p,i) in (addForm[f.prop]||[])" :key="i">
                    <div class="ro-plan__cell" @click="openDatePick(f.prop, i)">
                      <span :class="{'ro-val--ph': !p.date}">{{ p.date || '回款日期' }}</span>
                    </div>
                    <input class="ro-plan__cell ro-plan__cell--amt" type="number" v-model="p.amount" placeholder="金额（元）" />
                    <span class="ro-plan__del" @click="delPayPlan(i)">×</span>
                  </div>
                </div>
                <div class="ro-addplan" @click="addPayPlan">+ 添加回款计划</div>
              </div>

              <!-- 多选：chips -->
              <div class="ro-block" v-else-if="f.type==='chips'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <div class="ro-chips">
                  <span
                    class="ro-chip"
                    :class="{'ro-chip--on': isChipOn(addForm[f.prop], o.value)}"
                    v-for="o in f.options"
                    :key="o.value"
                    @click="toggleChip(f, o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>

              <!-- 时间区间：开始 ~ 结束 -->
              <!-- 【P33 修复】由裸文本输入框改为「滑动时间选择器」：
                   原输入框允许任意字符，用户极易输成 "0830"（少冒号），
                   id=68804 即因此落库 start_time=["0830","20:30"]。
                   选择器默认 08:30 / 20:30，从根源消除手输格式错误。 -->
              <div class="ro-block" v-else-if="f.type==='range'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <div class="ro-range">
                  <div class="ro-input ro-input--pick" @click="openTimePick(f.prop,'begin')">
                    <span :class="{'ro-pick--ph': !(addForm[f.prop] && addForm[f.prop].begin)}">
                      {{ (addForm[f.prop] && addForm[f.prop].begin) || '08:30' }}
                    </span>
                  </div>
                  <span class="ro-tilde">~</span>
                  <div class="ro-input ro-input--pick" @click="openTimePick(f.prop,'end')">
                    <span :class="{'ro-pick--ph': !(addForm[f.prop] && addForm[f.prop].end)}">
                      {{ (addForm[f.prop] && addForm[f.prop].end) || '20:30' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 数字：只读（如「市场价格」价目表定值）不可输入，带锁图标 -->
              <div class="ro-field" v-else-if="f.type==='number' && f.readonly" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val ro-val--right" :class="{'ro-val--ph': !addForm[f.prop]}">{{ addForm[f.prop] || (f.placeholder || '自动带出') }}</span>
                <span class="ro-lock"><van-icon name="lock" /></span>
              </div>

              <!-- 数字 -->
              <div class="ro-field" v-else-if="f.type==='number'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <input class="ro-input" type="number" v-model="addForm[f.prop]" :placeholder="f.placeholder || ('请输入'+f.label)" />
              </div>

              <!-- 文本域 -->
              <div class="ro-block" v-else-if="f.type==='textarea'" :key="f.prop">
                <span class="ro-label" v-if="f.label !== g.name"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <!-- 备注（remark）走自研富文本（04）；其余文本域保持轻量 textarea -->
                <RichEditor
                  v-if="f.prop === 'remark'"
                  :value="addForm[f.prop]"
                  :placeholder="f.placeholder || ('请输入'+f.label+'（选填）')"
                  @input="v => $set(addForm, f.prop, v)"
                />
                <div class="ro-textarea" v-else :class="{'ro-textarea--alone': f.label === g.name}">
                  <textarea v-model="addForm[f.prop]" :placeholder="f.placeholder || ('请输入'+f.label+'（选填）')"></textarea>
                </div>
              </div>

              <!-- 图片上传（本地单张，van-uploader + 自定义 after-read 直传业务接口） -->
              <div class="ro-block" v-else-if="f.type==='upload'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <van-uploader
                  v-model="addForm[f.prop + '_list']"
                  :after-read="(file) => afterReadUpload(f, file)"
                  :delete="() => onUploadDelete(f)"
                  :max-count="f.maxCount || 1"
                  :accept="f.accept || 'image/*'"
                />
              </div>

              <!-- 普通文本 -->
              <div class="ro-field" v-else :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <input class="ro-input" v-model="addForm[f.prop]" :placeholder="'请输入'+f.label" />
              </div>
              </template>
            </template>
          </div>
        </div>

          <!-- 调试信息：默认隐藏，仅在 URL 带 ?debug=1 时展示（生产/测试环境均不再暴露） -->
          <div class="ro-debug" v-if="debugOn && saveDebug">{{ saveDebug }}</div>
          <div class="ro-actions">
            <button type="button" class="ro-btn ro-btn--cancel" @click="closeForm">取消</button>
            <!-- 【P12】支付单双动作：存草稿 / 提交审批；其他单据保持单保存 -->
            <template v-if="isPaymentForm">
              <!-- 【P292】按用户 10-10 要求去掉「存草稿」：支付单只保留「提交审批」。
                   避免产生 status=3 的草稿单，也消除「先存草稿再提交」这条路径上
                   contracts_sn（关联财务合同）被状态机置空的歧义。 -->
              <button type="button" class="ro-btn ro-btn--save" :disabled="saving" @click="submitAdd('submit')">
                {{ saving ? '提交中…' : '提交审批' }}
              </button>
            </template>
            <button v-else type="button" class="ro-btn ro-btn--save" :disabled="saving" @click="submitAdd()">
              {{ saving ? '保存中…' : '保存' }}
            </button>
          </div>
        </div>
      </van-popup>
    </div>

    <!-- 下拉选择弹层 -->
    <van-action-sheet
      v-model="sheetVisible"
      :title="sheetTitle"
      :actions="sheetOptions"
      close-on-click-action
      @select="onSheetSelect"
    />

    <!-- 单据列表排序 -->
    <van-action-sheet
      v-model="billSortSheetVisible"
      title="排序方式"
      :actions="billSortOptions"
      close-on-click-action
      @select="onBillSortSelect"
    />

    <!-- 【P24-5/7】支付单筛选面板：支付时间 / 支付方式 / 支付类型 / 支付类型明细 / 状态 / 受理人 -->
    <van-popup v-model="billFilterSheetVisible" round position="bottom" :style="{ height: '76%' }">
      <div class="cd-form">
        <div class="cd-form__bar">
          <span class="cd-form__title">筛选</span>
          <van-icon name="cross" class="cd-form__close" @click="billFilterSheetVisible=false" />
        </div>
        <div class="cd-form__body">
          <div class="bf-group" v-for="f in (billTab && billTab.filters) || []" :key="f.prop">
            <div class="bf-label">{{ f.label }}</div>
            <!-- 日期区间 -->
            <div v-if="f.type === 'daterange'" class="bf-range">
              <input class="bf-input" type="date" v-model="billFilter[f.beginKey]" />
              <span class="bf-sep">至</span>
              <input class="bf-input" type="date" v-model="billFilter[f.endKey]" />
            </div>
            <!-- 下拉（用 action sheet 简化实现：点击后弹出可选值） -->
            <div v-else-if="f.type === 'select'" class="bf-select" @click="openFilterOption(f)">
              <span :class="{ 'bf-placeholder': !filterOptionText(f) }">{{ filterOptionText(f) || ('请选择' + f.label) }}</span>
              <van-icon name="arrow-down" />
            </div>
            <!-- 关键字 -->
            <input v-else-if="f.type === 'search'" class="bf-input bf-input--full" type="text"
              v-model="billFilter[f.prop]" :placeholder="f.placeholder || '请输入关键字'" />
          </div>
          <div class="bf-actions">
            <div class="bf-btn bf-btn--ghost" @click="onBillFilterReset">重置</div>
            <div class="bf-btn bf-btn--primary" @click="onBillFilterConfirm">确定</div>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 【P24-5】筛选下拉：选项选择 -->
    <van-action-sheet
      v-model="filterPickSheetVisible"
      :title="filterPickLabel"
      :actions="filterPickActions"
      close-on-click-action
      @select="onFilterPickSelect"
    />

    <!-- 日期选择器 -->
    <van-popup v-model="dateVisible" position="bottom">
      <van-datetime-picker
        v-model="dateCurrent"
        type="date"
        :min-date="minDate"
        @confirm="onDateConfirm"
        @cancel="dateVisible=false"
      />
    </van-popup>

    <!-- 【P33】使用时间选择器（滑动，替代裸文本输入）默认 08:30 / 20:30 -->
    <van-popup v-model="timeVisible" position="bottom" round>
      <van-datetime-picker
        v-model="timeCurrent"
        type="time"
        title="选择使用时间"
        @confirm="onTimeConfirm"
        @cancel="timeVisible=false"
      />
    </van-popup>

    <!-- 编辑合同 -->
    <van-popup v-model="editVisible" round position="bottom" :style="{ height: '88%' }">
      <div class="cd-form">
        <div class="cd-form__bar">
          <span class="cd-form__title">编辑合同</span>
          <van-icon name="cross" class="cd-form__close" @click="editVisible=false" />
        </div>
        <div class="cd-form__body">
          <van-form @submit="submitEdit">
            <van-field v-model="editForm.name" label="合同名称" placeholder="请输入合同名称" required />
            <van-field v-model="editForm.serial_number" label="合同编号" placeholder="留空自动生成" />
            <van-field v-model="editForm.sales_amount" label="实际销售金额" type="number" placeholder="请输入实际销售金额" />
            <van-field v-model="editForm.room_amount" label="会议室金额" type="number" placeholder="请输入会议室金额" />
            <van-field v-model="editForm.value_added_services_a_amount" label="订餐金额" type="number" placeholder="请输入订餐金额" />
            <van-field v-model="editForm.value_added_services_b_amount" label="茶歇金额" type="number" placeholder="请输入茶歇金额" />
            <van-field v-model="editForm.other_amount" label="其他金额" type="number" placeholder="请输入其他金额" />
            <van-field v-model="editForm.signing_date" label="签订日期" placeholder="YYYY-MM-DD" />
            <van-field v-model="editForm.start_date" label="开始时间" placeholder="YYYY-MM-DD" />
            <van-field v-model="editForm.end_date" label="结束时间" placeholder="YYYY-MM-DD" />
            <van-field v-model="editForm.contacts" label="联系人" placeholder="请输入联系人" />
            <van-field v-model="editForm.phone" label="手机" placeholder="请输入手机号" />
            <van-field v-model="editForm.weixin" label="微信" placeholder="请输入微信号" />
            <van-field v-model="editForm.remark" label="备注" type="textarea" rows="3" placeholder="选填" />
          </van-form>
        </div>
        <div class="cd-form__actions">
          <van-button block plain @click="editVisible=false">取消</van-button>
          <van-button block type="info" native-type="submit" :loading="saving" loading-text="保存中">保存</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 删除确认（全站统一版：ConfirmDialog 居中弹窗） -->
    <ConfirmDialog
      v-model="showDel"
      :desc="delDialogDesc"
      :loading="saving"
      loading-text="删除中"
      @confirm="confirmDelete"
    />

    <!-- 全站统一轻提示（替代散落的 Vant $toast / Element Message） -->
    <CrmNotice ref="notice" />

  </div>
</template>

<script>
import contractDetailMixin from "../../mixins/contractDetailMixin.js";
// 【P24-5】支付单筛选：受理人下拉
import { getPaymentOwnerOptions } from "../../api/market.js";
// 单据页面配置聚合出口 + 合同阶段映射（1待执行 2已预定 3已支付 4已全款）
import {
  CONTRACT_STAGES,
  stageIndex,
  stageLabel,
  stagePercent
} from "../contract/index.js";

// 合同详情字段分组：对齐后台 admin/CRMOrdersController V5 详情配置
// 【P24-2】按业务 22 项对照表严格重排 money 字段（含 4 组重复口径，是业务有意为之：
//   前 9 项是「本单口径」，第 13-16 项是「订单口径」，同名不同来源，需全部展示）。
//   注意「充值金额」正确字段是 total_deposits，旧值 recharge_amount 已废弃。
const FIELD_GROUPS = [
  {
    key: "biz",
    title: "业务信息",
    money: [
      { label: "实际销售金额", prop: "sales_amount" },
      { label: "会议室金额", prop: "room_amount" },
      { label: "订餐金额", prop: "value_added_services_a_amount" },
      { label: "茶歇金额", prop: "value_added_services_b_amount" },
      { label: "其他金额", prop: "other_amount" },
      { label: "支付金额", prop: "payment_amount" },
      { label: "待支付金额", prop: "pending_payment_amount" },
      { label: "充值金额", prop: "total_deposits" },
      { label: "当前余额", prop: "balance_amount" },
      { label: "订单总金额", prop: "total_amount" },
      { label: "订单支付金额", prop: "total_payment_amount" },
      { label: "订单退款金额", prop: "financial_refun_amount" },
      { label: "订单实际支付金额", prop: "payment_amount" },
      { label: "订单待支付金额", prop: "pending_payment_amount" },
      { label: "充值金额", prop: "total_deposits" },
      { label: "当前余额", prop: "balance_amount" },
      { label: "非余额支付", prop: "base_payment_amount" },
      { label: "余额支付", prop: "recharge_payment_amount" },
      { label: "退款现金给客户", prop: "financial_refun_cash_amount" },
      { label: "退款到订单的余额", prop: "financial_refun_balance_amount" },
      { label: "转移来的余额", prop: "financial_transfer_in_balance_amount" },
      { label: "转移出的余额", prop: "financial_transfer_out_balance_amount" }
    ],
    rows: [
      { label: "关联商机", prop: "opportunities_name" },
      { label: "签订日期", prop: "signing_date" },
      { label: "开始时间", prop: "start_date" },
      { label: "结束时间", prop: "end_date" },
      { label: "联系人", prop: "contacts" },
      { label: "手机", prop: "phone" },
      { label: "微信", prop: "weixin" },
      { label: "备注", prop: "remark", strip: true }
    ]
  },
  {
    key: "sys",
    title: "系统信息",
    money: [],
    rows: [
      { label: "负责人", prop: "owner_name" },
      { label: "创建人", prop: "create_user_name" },
      { label: "更新人", prop: "update_user_name" },
      { label: "创建时间", prop: "create_time" },
      { label: "更新时间", prop: "update_time" }
    ]
  }
];

// 合同详情页：模板与样式保留在此组件，所有业务逻辑下沉到 contractDetailMixin.js
// （可被本组件独立路由使用，也可被 client.vue 以 <contract-detail :contract-sn> 内嵌复用）
// 全站统一删除确认弹窗（居中弹窗），与本页 .cd-del-pop 旧样式二选一，现已统一
import ConfirmDialog from "@/components/ConfirmDialog.vue";
// 全站统一轻提示（02）：替代 Vant $toast / Element Message，保证失败也能展示后端 msg 原文
import CrmNotice from "@/components/CrmNotice.vue";
// 自研富文本编辑器（04）：备注富文本化，输出 HTML，不引第三方库
import RichEditor from "@/components/RichEditor.vue";

export default {
  name: "ContractDetail",
  components: { ConfirmDialog, CrmNotice, RichEditor },
  mixins: [contractDetailMixin],
  props: {
    // 内嵌场景由父组件传入 sn；独立路由场景不传，由 mixin 读取 $route.params.sn
    contractSn: { type: String, default: "" },
    // 快捷入口某单据 tile 进入时，自动打开对应单据 tab 的 key（如 payment / refund / room）
    pendingBill: { type: String, default: "" },
    // 内嵌于 client.vue 合同时为 true：编辑统一回退到父级列表编辑弹层，不再使用本组件内联编辑弹窗
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      /* P57 市场部门只读：合同详情内不提供 编辑合同 / 单据新增 / 编辑 / 提交 / 删除 */
      mkReadonly: true,
      // 当前打开的独立单据页面（tab.key）；null = 停留在合同总览
      billPage: null,
      // 三组合同字段是否展开（全部默认收起，腾出空间放单据图标）
      groupOpen: { biz: false, sys: false },
      // 单据页下拉刷新中
      billRefreshing: false,
      // 【P24-1】总览区（Hero + 合同信息 + 业务单据宫格）下拉刷新中
      overviewRefreshing: false,
      // 单据列表本地搜索关键字
      billSearch: "",
      // 单据列表排序方式：'' | 'amount-desc' | 'amount-asc' | 'date-desc' | 'date-asc'
      billSort: "",
      // 排序选择弹窗
      billSortSheetVisible: false,
      // 排序选项
      billSortOptions: [],
      // 【P24-5/7】服务端筛选面板（支付单等声明了 filters 的单据使用）
      billFilterSheetVisible: false,
      // 筛选表单值：{ payment_date_begin, payment_date_end, payment_method, payment_type, payment_product, status, owner_id }
      billFilter: {},
      // 筛选下拉字典：{ payment_method:[{value,text}], ... }
      billFilterOptions: {},
      // 筛选条件是否已生效（有值即视为生效，用于按钮高亮）
      billFilterActive: false,
      // 【P24-5】筛选下拉的二级选择面板
      filterPickSheetVisible: false,
      filterPickLabel: "",
      filterPickActions: [],
      filterPickProp: "",
      fieldGroups: FIELD_GROUPS,
      // 单据详情（只读弹层）：点列表卡片弹出
      detailShow: false,
      detailRow: null,
      // 【P16】财务合同详情：底部关联区块（会议室/回款单/发票/退款记录）
      detailRelated: []
    };
  },
  created() {
    // 从快捷入口某单据 tile 进入时，自动打开对应单据 tab
    if (this.pendingBill && this.tabConfigs && this.tabConfigs.some(t => t.key === this.pendingBill)) {
      this.billPage = this.pendingBill;
    }
  },
  computed: {
    // 调试条开关：仅在 URL 显式带 ?debug=1 或 hash 内含 debug=1 时展示
    // （默认隐藏，避免把「1. 进入 submitAdd」这类内部状态暴露给门店用户）
    debugOn() {
      const q = (window.location.search || "") + (window.location.hash || "");
      return /[?&]debug=1(&|$)/.test(q);
    },
    // 当前单据页对应的配置对象
    billTab() {
      if (!this.billPage) return null;
      return (this.tabConfigs || []).find(t => t.key === this.billPage) || null;
    },
    // 单据详情（只读）：按 group 分组，只渲染有值的字段；金额类字段用 billMoney 格式化
    detailGroups() {
      const row = this.detailRow;
      if (!row) return [];
      const fields = (this.billTab && this.billTab.fields) || [];
      const map = {};
      const order = [];
      fields.forEach(f => {
        if (!f || !f.prop) return;
        // plans（回款计划多行）/ upload（图片）/ hidden 不在只读详情里展示
        if (f.type === "hidden" || f.type === "plans" || f.type === "upload") return;
        // 【P13-fix】走 rowDisp：客户/商机/会议室订单等关联字段按 options 反解名称，不再显示 SN/ID
        const v = this.rowDisp(this.billTab, f, row);
        if (v === "" || v === null || v === undefined) return;
        const g = f.group || "信息";
        if (!map[g]) { map[g] = { name: g, fields: [] }; order.push(g); }
        map[g].fields.push({
          label: f.label,
          value: v,
          money: /amount|money|price/i.test(f.prop)
        });
      });
      return order.map(k => map[k]);
    },
    // 单据列表：把每行组装成统一结构（标题/金额/状态/单号/日期/详情/计划），9 个单据页共用一套渲染
    billRows() {
      const tab = this.billTab;
      if (!tab) return [];
      const rows = this.lists[tab.key] || [];
      return rows.map(row => {
        const m = this.billMeta(tab, row);
        // 卡片两行摘要：第一行「编号 · 日期」，第二行取前两个详情字段（muted）
        let r1 = "";
        if (m.noText) r1 += "编号：" + m.noText;
        if (m.dateText) r1 += (r1 ? " · " : "") + m.dateText;
        if (!r1) r1 = "—";
        let r2 = (m.details || []).slice(0, 2)
          .map(f => f.label + "：" + this.rowDisp(tab, f, row))
          .join("  ·  ");
        if (!r2) r2 = "点击查看完整详情";
        return {
          row: row,
          title: this.rowTitle(tab, row),
          amountNum: m.amountNum,
          amountText: m.amountText,
          noText: m.noText,
          dateText: m.dateText,
          status: m.status,
          statusClass: this.billStatusClass(m.status),
          details: m.details,
          plans: row.payment_plans || [],
          row1: r1,
          row2: r2
        };
      });
    },
    // 单据列表：本地搜索 + 排序
    // 【P24-5/7】若该单据声明了 filters（如支付单），筛选/关键字由服务端处理，
    // 本地仅保留排序，避免服务端结果被本地二次过滤掉（如 payment_date 区间）。
    filteredBillRows() {
      let arr = (this.billRows || []).slice();
      const tab = this.billTab || {};
      const serverFiltered = !!(tab.filters && tab.filters.length);
      const kw = (this.billSearch || "").trim();
      if (kw && !serverFiltered) {
        const lower = kw.toLowerCase();
        arr = arr.filter(item => {
          const text = [item.title, item.row1, item.row2, item.status].join(" ");
          return text.toLowerCase().indexOf(lower) >= 0;
        });
      }
      const sort = this.billSort || "";
      if (sort === "amount-desc") {
        arr.sort((a, b) => (b.amountNum === null || b.amountNum === undefined ? -Infinity : b.amountNum) - (a.amountNum === null || a.amountNum === undefined ? -Infinity : a.amountNum));
      } else if (sort === "amount-asc") {
        arr.sort((a, b) => (a.amountNum === null || a.amountNum === undefined ? Infinity : a.amountNum) - (b.amountNum === null || b.amountNum === undefined ? Infinity : b.amountNum));
      } else if (sort === "date-desc") {
        arr.sort((a, b) => String(b.dateText || "").localeCompare(String(a.dateText || "")));
      } else if (sort === "date-asc") {
        arr.sort((a, b) => String(a.dateText || "").localeCompare(String(b.dateText || "")));
      }
      return arr;
    },
    // 删除确认弹窗：标题/名称随单据类型变化
    // 【P205 1.1.02.001】兜底文案统一为「会议室订单」
    delTitle() {
      return this.billTab ? this.billTab.title : "会议室订单";
    },
    delName() {
      const r = this.delRow;
      if (!r) return "—";
      if (this.billTab) return this.rowTitle(this.billTab, r);
      return r.meeting_room_name || r.meeting_room_sn || "—";
    },
    // 统一删除确认弹窗的说明文案（全站同模板：确定要删除该「XX」吗？删除后不可恢复。）
    delDialogDesc() {
      return '确定要删除该「' + this.delTitle + '」吗？删除后不可恢复。';
    },
    // 单据页顶部汇总：条数 + 合计金额（有金额字段的单据才显示合计）
    billTotal() {
      let sum = 0;
      let has = false;
      this.billRows.forEach(r => {
        if (r.amountNum !== null && r.amountNum !== undefined) {
          sum += r.amountNum;
          has = true;
        }
      });
      return { count: this.billRows.length, sum: sum, hasSum: has };
    },
    // 基于过滤后的单据列表计算汇总
    filteredBillTotal() {
      let sum = 0;
      let has = false;
      this.filteredBillRows.forEach(r => {
        if (r.amountNum !== null && r.amountNum !== undefined) {
          sum += r.amountNum;
          has = true;
        }
      });
      return { count: this.filteredBillRows.length, sum: sum, hasSum: has };
    },
    stages() {
      return CONTRACT_STAGES;
    },
    stageIdx() {
      return stageIndex(this.contract && this.contract.stage);
    },
    stagePct() {
      return stagePercent(this.contract && this.contract.stage);
    },
    stageText() {
      const c = this.contract || {};
      return stageLabel(c.stage, c.stage_name);
    }
  },
  methods: {
    /* 【P57】市场部门只读兜底：任何写操作入口被绕过时统一提示并阻断 */
    mkRoDeny() {
      this.$toast("市场部门仅可新增与分配客户，无此操作权限");
      return false;
    },
    // 编辑合同：内嵌场景回退到父级（client.vue 列表编辑弹层），独立路由场景走本组件内联编辑
    onEditContractClick() {
      if (this.mkReadonly) { return this.mkRoDeny(); }
      if (this.embedded) {
        this.$emit("edit");
      } else {
        this.editContract();
      }
    },
    // 单据页下拉刷新：重新拉全部单据列表
    onBillRefresh() {
      this.loadAllLists()
        .catch(() => {})
        .then(() => { this.billRefreshing = false; });
    },
    // 【P24-1】总览区下拉刷新：同时刷新「合同详情主数据」与「各业务单据数量（宫格角标/括号数）」
    onOverviewRefresh() {
      const done = () => { this.overviewRefreshing = false; };
      return Promise.all([
        this.fetchDetail().catch(() => {}),
        this.loadAllLists().catch(() => {})
      ]).then(done);
    },
    // 打开单据排序选择 / 【P24-5】支付单打开服务端筛选面板
    openSortSheet() {
      const tab = this.billTab;
      if (tab && tab.filters && tab.filters.length) {
        this.loadBillFilterOptions();
        this.billFilterSheetVisible = true;
        return;
      }
      this.billSortOptions = [
        { name: "默认排序", value: "" },
        { name: "金额从高到低", value: "amount-desc" },
        { name: "金额从低到高", value: "amount-asc" },
        { name: "日期从新到旧", value: "date-desc" },
        { name: "日期从旧到新", value: "date-asc" }
      ].map(o => ({ ...o, className: this.billSort === o.value ? "van-action-sheet__item--active" : "" }));
      this.billSortSheetVisible = true;
    },
    // 【P24-5】拉筛选下拉字典（支付方式/支付类型/支付产品/状态/受理人）
    loadBillFilterOptions() {
      const dict = {
        payment_method: [
          { value: 1, text: "收钱吧" }, { value: 2, text: "网银" }, { value: 3, text: "快团团" },
          { value: 4, text: "美团" }, { value: 5, text: "现金" }, { value: 6, text: "余额" }, { value: 7, text: "其他" }
        ],
        payment_type: [
          { value: 1, text: "订金" }, { value: 2, text: "全款" }, { value: 3, text: "中期款" }, { value: 4, text: "尾款" }
        ],
        payment_product: [
          { value: 1, text: "会议室" }, { value: 2, text: "餐饮" }, { value: 3, text: "茶歇" },
          { value: 4, text: "客户充值" }, { value: 5, text: "其他服务" }
        ],
        status: [
          { value: 0, text: "审核中" }, { value: 1, text: "审核通过" }, { value: 2, text: "审核驳回" }, { value: 3, text: "待提交" }
        ],
        owner: []
      };
      this.billFilterOptions = dict;
      // 受理人（财务人员）异步补全，失败不阻断
      getPaymentOwnerOptions()
        .then(res => {
          const arr = (res && (res.data || res)) || [];
          this.$set(this.billFilterOptions, "owner", Array.isArray(arr) ? arr : []);
        })
        .catch(() => {});
    },
    // 【P24-5】筛选面板「确定」：写回 billFilter 并重拉列表
    onBillFilterConfirm() {
      const f = this.billFilter || {};
      const has = Object.keys(f).some(k => f[k] !== "" && f[k] !== null && f[k] !== undefined);
      this.billFilterActive = has;
      this.billFilterSheetVisible = false;
      const extra = {};
      Object.keys(f).forEach(k => { if (f[k] !== "" && f[k] !== null && f[k] !== undefined) extra[k] = f[k]; });
      this.reloadBillList(this.billTab, extra);
    },
    // 【P24-5】筛选面板「重置」
    onBillFilterReset() {
      this.billFilter = {};
      this.billFilterActive = false;
      this.reloadBillList(this.billTab, {});
    },
    // 【P24-7】关键字搜索：透传后端 searchText（支付单走 search_key LIKE），并保留本地即时过滤
    onBillSearchSubmit() {
      const tab = this.billTab;
      if (!tab || !tab.filters) return;
      const kw = (this.billSearch || "").trim();
      this.reloadBillList(tab, kw ? { searchText: kw } : {});
    },
    // 【P24-5】打开筛选下拉的二级选择面板
    openFilterOption(f) {
      const src = (f.optionsFn && this.billFilterOptions[f.optionsFn]) || [];
      this.filterPickProp = f.prop;
      this.filterPickLabel = f.label;
      const cur = this.billFilter[f.prop];
      this.filterPickActions = src.map(o => ({
        name: o.text,
        value: o.value,
        className: String(cur) === String(o.value) ? "van-action-sheet__item--active" : ""
      }));
      this.filterPickSheetVisible = true;
    },
    // 【P24-5】筛选下拉当前展示文本
    filterOptionText(f) {
      const v = this.billFilter[f.prop];
      if (v === "" || v === null || v === undefined) return "";
      const src = (f.optionsFn && this.billFilterOptions[f.optionsFn]) || [];
      const hit = src.find(o => String(o.value) === String(v));
      return hit ? hit.text : String(v);
    },
    // 【P24-5】选中筛选下拉值
    onFilterPickSelect(item) {
      const p = this.filterPickProp;
      if (p) this.$set(this.billFilter, p, item.value);
      this.filterPickSheetVisible = false;
    },
    // 选中排序方式
    onBillSortSelect(item) {
      this.billSort = item.value || "";
      this.billSortSheetVisible = false;
    },
    // 三组字段独立展开/收起
    toggleGroup(key) {
      this.$set(this.groupOpen, key, !this.groupOpen[key]);
    },
    // 回款计划是否为「当前必填」：字段声明 requiredWhen='notFree' 时，仅当本单非免费才显示星标
    // （免费单后端不校验回款计划，前端也不应误导用户去填）
    isPlanRequired(f) {
      if (!f) return false;
      if (f.required) return true;
      if (f.requiredWhen !== "notFree") return false;
      return String(this.addForm.is_free) !== "是";
    },
    // 金额字段：空值显示 —，否则 ¥ + 千分位
    moneyText(prop) {
      const c = this.contract || {};
      const v = c[prop];
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (isNaN(n)) return String(v);
      return "¥" + this.fmtMoney(n);
    },
    // 文本字段
    textOf(prop) {
      const c = this.contract || {};
      const v = c[prop];
      if (v === null || v === undefined || v === "") return "—";
      return String(v);
    },
    // 富文本（备注）去标签
    plainText(v) {
      if (v === null || v === undefined) return "—";
      const s = String(v)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return s || "—";
    },
    // 点击宫格图标 → 打开该单据的独立页面（client 内切换，不跳路由）
    openBill(tab) {
      this.billPage = tab.key;
      this.billFormMode = null;
    },
    closeBill() {
      this.billPage = null;
      this.billFormMode = null;
    },
    // 底部弹窗表单：遮罩/关闭时同步 billFormMode（v-model 手写形式，避免直接改 mixin 状态）
    onFormPopupInput(v) {
      if (!v) this.closeForm();
    },
    // 点列表卡片 → 打开只读详情（编辑走卡片上的「编辑」按钮）
    openRowDetail(row) {
      if (!row) return;
      this.detailRow = row;
      this.detailRelated = [];
      this.detailShow = true;
      // 【P16】财务合同：详情聚合接口（基本信息补全 + 关联会议室/回款单/发票/退款记录）
      const tab = this.billTab;
      if (tab && typeof tab.detailFn === "function") {
        tab.detailFn({ sn: row.sn }).then(res => {
          const d = (res && (res.data || res)) || {};
          const info = d.info || {};
          this.detailRow = Object.assign({}, row, info);
          this.detailRelated = (tab.related || []).map(r => {
            const found = (d.related || []).find(x => x.key === r.key);
            return { key: r.key, label: r.label, items: (found && found.items) || [] };
          });
        }).catch(() => {});
      }
    },
    // 详情页底部「编辑」：关掉详情，直接进编辑表单
    onDetailEdit() {
      if (this.mkReadonly) { return this.mkRoDeny(); }
      const row = this.detailRow;
      this.detailShow = false;
      this.detailRow = null;
      if (row) this.openForm("edit", row);
    },
    // 列表卡片 / 「详情」按钮 / 「编辑」按钮 → 打开整页表单（add=新增空白，edit=回填当前行）
    openForm(mode, row) {
      /* 【P57】市场部门只读：新增/编辑表单是附件上传等写侧能力的唯一入口，直接封死 */
      if (this.mkReadonly) { return this.mkRoDeny(); }
      if (mode === "add") {
        this.openAdd(this.billTab);
      } else {
        this.openEdit(row);
      }
    },
    // 关闭表单页 → 退回列表
    closeForm() {
      this.billFormMode = null;
    },
    // 顶部导航返回：表单页 → 退回列表；单据页 → 退回合同总览；否则关闭整个详情
    onNavLeft() {
      if (this.billFormMode) {
        this.closeForm();
      } else if (this.billTab) {
        this.closeBill();
      } else {
        this.goBack();
      }
    }
  }
};
</script>

<style scoped>
/* 【P24-1】总览区下拉刷新容器：清零内边距，保证 Hero 仍是通栏（否则会被 Vant 默认 padding 挤窄） */
.cd-page > .van-pull-refresh {
  padding: 0;
  margin: 0;
}
.cd-page {
  position: relative;
  /* 与 client-root 一致：扣掉顶栏 + 底部 tabbar 占位，避免整页多余滚动 */
  min-height: calc(100vh - 115px);
  background: #f5f6f8;
  padding-bottom: 24px;
}

/* 顶部导航（白底黑字） */
.cd-nav {
  background: #fff;
}
.cd-nav ::v-deep .van-nav-bar__title,
.cd-nav ::v-deep .van-icon,
.cd-nav ::v-deep .van-nav-bar__text {
  color: #1d2129;
}
.cd-nav ::v-deep .van-nav-bar__content {
  background: #fff;
}
.cd-nav ::v-deep .van-hairline--bottom::after {
  border-color: #f2f3f5;
}

/* Hero 头部 */
.cd-hero {
  background: linear-gradient(135deg, #2b5fd9 0%, #4a8af0 100%);
  padding: 18px 18px 22px;
  color: #fff;
}
/* 主金额：单独一行放大 */
.cd-hero__main {
  margin-bottom: 16px;
}
.cd-hero__main-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
}
.cd-hero__main-value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  margin-top: 4px;
  color: #fff;
  word-break: break-all;
}
/* 四个金额指标 */
.cd-hero__metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 12px 4px;
}
.cd-metric {
  text-align: center;
  padding: 0 2px;
}
.cd-metric__label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
}
.cd-metric__value {
  font-size: 14px;
  font-weight: 700;
  margin-top: 4px;
  color: #fff;
}

/* 信息面板（上浮卡片） */
.cd-info {
  margin: -12px 12px 0;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px 16px;
  box-shadow: 0 4px 14px rgba(31, 58, 110, 0.08);
  position: relative;
}
.cd-info__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}
.cd-info__item {
  display: flex;
  flex-direction: column;
}
.cd-info__label {
  font-size: 12px;
  color: #969799;
  margin-bottom: 3px;
}
.cd-info__value {
  font-size: 14px;
  color: #323233;
  font-weight: 500;
  word-break: break-all;
}
.cd-info__item--full {
  grid-column: 1 / -1;
}
.cd-edit-btn {
  margin-top: 14px;
  width: 100%;
  border-color: #2b5fd9;
  color: #2b5fd9;
  border-radius: 8px;
}

/* 加载：全站统一上下 + 左右居中 */
.cd-loading {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 资料 Tab 紧凑信息列表 */
.cd-info-list {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  overflow: hidden;
  margin-top: 4px;
}
.cd-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  min-height: 42px;
  border-bottom: 1px solid #f2f3f5;
}
.cd-info-row:last-child {
  border-bottom: none;
}
.cd-info-row__label {
  font-size: 13px;
  color: #646566;
}
.cd-info-row__value {
  font-size: 13px;
  color: #323233;
  text-align: right;
  flex: 1;
  margin-left: 12px;
  word-break: break-all;
}

.cd-tabs ::v-deep .van-tab {
  padding: 0 16px;
  flex: 0 0 auto;
}
.cd-page .van-cell-group {
  margin-top: 12px;
}

/* Tab 内容 */
.tab-body {
  padding: 12px;
}

/* Tab 工具条：对齐 client.vue customer-header（返回 + 搜索 + 筛选 + 新增） */
.cd-tabbar {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 14px 8px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(31, 58, 110, 0.05);
}
.cd-tabbar__top {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cd-tabbar__left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  cursor: pointer;
}
.cd-tabbar__back {
  font-size: 18px;
  color: #2b5fd9;
}
.cd-tabbar__title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cd-tabbar__search {
  flex: 1;
  min-width: 0;
  position: relative;
}
.cd-tabbar__search input {
  width: 100%;
  height: 34px;
  line-height: 34px;
  border-radius: 17px;
  border: none;
  background: #f5f6f8;
  font-size: 13px;
  padding: 0 28px 0 32px;
  box-sizing: border-box;
  outline: none;
}
.cd-tabbar__search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #c0c4cc;
}
.cd-tabbar__search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #c0c4cc;
  cursor: pointer;
}
.cd-tabbar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.cd-tabbar__btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: #1d2129;
  cursor: pointer;
}
.cd-tabbar__btn .van-icon {
  font-size: 18px;
  color: #4e5969;
}
.cd-tabbar__btn--primary .van-icon {
  color: #2b5fd9;
}
/* 【P24-5】筛选已生效：高亮 */
.cd-tabbar__btn--active,
.cd-tabbar__btn--active .van-icon {
  color: #2b5fd9;
}
.cd-tabbar__summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 13px;
  color: #4e5969;
}

/* 弹层 */
.cd-form {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.cd-form__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #f2f3f5;
}
.cd-form__title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}
.cd-form__close {
  font-size: 20px;
  color: #969799;
}
.cd-form__body {
  flex: 1;
  overflow-y: auto;
  padding: 4px 4px 0;
}
/* 【P24-5】支付单筛选面板 */
.bf-group {
  padding: 12px 16px 4px;
}
.bf-label {
  font-size: 13px;
  color: #646566;
  margin-bottom: 8px;
}
.bf-input {
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #1d2129;
  background: #fafafa;
  box-sizing: border-box;
}
.bf-input--full {
  width: 100%;
}
.bf-range {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bf-sep {
  font-size: 13px;
  color: #969799;
  flex: none;
}
.bf-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #1d2129;
  background: #fafafa;
}
.bf-select .van-icon {
  color: #969799;
  font-size: 14px;
}
.bf-placeholder {
  color: #c8c9cc;
}
.bf-actions {
  display: flex;
  gap: 12px;
  padding: 18px 16px 24px;
  position: sticky;
  bottom: 0;
  background: #fff;
}
.bf-btn {
  flex: 1;
  height: 44px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}
.bf-btn--ghost {
  border: 1px solid #ebedf0;
  color: #323233;
  background: #fff;
}
.bf-btn--primary {
  color: #fff;
  background: #1989fa;
}
.cd-form__actions {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid #f2f3f5;
}
.cd-form__actions .van-button {
  flex: 1;
  border-radius: 8px;
}

/* ===== 添加弹层：分区卡片 + 自定义表单行（对齐 room_order_form_preview） ===== */
.cd-form--room .cd-form__body {
  background: #f5f6f8;
  padding: 10px 12px 12px;
}

/* 去掉卡片形式：无背景 / 无圆角 / 无阴影 / 无蓝条，字段直接平铺 */
.ro-group {
  background: transparent;
  border-radius: 0;
  margin-bottom: 10px;
  overflow: visible;
  box-shadow: none;
  border: none;
  border-left: none;
  padding: 0;
}
/* 分组标题：与客户表单 .form-divider 完全一致（13px 600 + 左 3px 蓝条 + 浅灰底） */
.ro-group__hd {
  font-size: 13px;
  font-weight: 600;
  color: #2b5fd9;
  padding: 14px 16px 8px;
  margin-top: 6px;
  border-left: 3px solid #2b5fd9;
  background: #fafbfc;
  display: block;
}
.ro-group__hd::before {
  display: none;
}
/* 只读详情弹层：表单已平铺，但详情是「看」，保留分组卡片（蓝点式分组标题） */
.ro-group--card {
  background: #fff;
  border-radius: 16px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(20, 40, 80, 0.06);
  padding: 0 14px 10px;
}
.ro-group--card .ro-group__hd {
  margin-top: 0;
  border-left: none;
  background: transparent;
  padding: 14px 0 10px;
  color: #1d2129;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 7px;
}
.ro-group--card .ro-group__hd::before {
  display: block;
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2b5fd9;
  flex: none;
}
.ro-group--card .ro-field {
  padding: 6px 0;
  border-top: 1px solid #f2f3f5;
}

/* 单行：label + 值/输入 + 箭头 */
.ro-field {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 16px;
  border-top: 1px solid #f5f6f8;
  background: #fff;
}
.ro-group .ro-field:first-of-type,
.ro-group .ro-block:first-of-type {
  border-top: none;
}
/* 标题正下方的首行不画线（:first-of-type 被 hd 占位，这里显式兜底） */
.ro-group__hd + .ro-field,
.ro-group__hd + .ro-block {
  border-top: none;
}
.ro-label {
  width: 88px;
  flex: none;
  font-size: 14px;
  color: #646566;
}
.ro-req {
  color: #ee0a24;
  font-style: normal;
  margin-right: 2px;
}
.ro-val {
  flex: 1;
  font-size: 14px;
  color: #323233;
  display: flex;
  align-items: center;
  word-break: break-all;
}
.ro-val--ph {
  color: #c8c9cc;
}
/* 只读详情：值右对齐（表单里是左对齐输入，详情是展示） */
.ro-val--right {
  justify-content: flex-end;
  text-align: right;
}
/* 详情无字段时的占位 */
.ro-empty {
  padding: 34px 0;
  text-align: center;
  font-size: 13px;
  color: #8c8f94;
}
.ro-arrow {
  margin-left: auto;
  color: #c8c9cc;
  font-size: 15px;
  padding-left: 6px;
}
.ro-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #323233;
  background: transparent;
  width: 100%;
  padding: 0;
  -webkit-appearance: none;
}
.ro-input::placeholder {
  color: #c8c9cc;
}

/* 块级：label 独占一行（chips / range / textarea） */
.ro-block {
  padding: 10px 16px 12px;
  border-top: 1px solid #f5f6f8;
  background: #fff;
}
.ro-block .ro-label {
  width: auto;
  display: block;
  margin-bottom: 8px;
}

/* 时间区间 */
.ro-range {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ro-range .ro-input {
  height: 40px;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 0 10px;
  text-align: center;
}
.ro-tilde {
  color: #c8c9cc;
  flex: none;
}

/* 【P33】使用时间：点击弹出滑动时间选择器（替代裸文本输入，消除手输格式错误） */
.ro-input--pick {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #323233;
  font-size: 14px;
  height: 40px;
  background: #f7f8fa;
  border-radius: 8px;
}
.ro-pick--ph { color: #c8c9cc; }

/* 多选 chips */
.ro-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ro-chip {
  font-size: 13px;
  padding: 6px 13px;
  border-radius: 16px;
  background: #f2f3f5;
  color: #646566;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.ro-chip--on {
  background: #eef2fb;
  color: #2b5fd9;
  border-color: #b9cdf5;
  font-weight: 600;
}
/* 【P9】全选 chip：虚线边框区分 */
.ro-chip--all {
  border: 1px dashed #b9cdf5;
  background: #fff;
  color: #2b5fd9;
}
.ro-chip--all.ro-chip--on {
  background: #2b5fd9;
  color: #fff;
  border-style: solid;
  border-color: #2b5fd9;
}

/* 回款计划行 */
.ro-plans {
  margin-top: 2px;
}
.ro-plan {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.ro-plan__cell {
  flex: 1;
  height: 40px;
  background: #f7f8fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-size: 13px;
  color: #323233;
  border: none;
  outline: none;
  width: 100%;
  min-width: 0;
}
.ro-plan__cell--amt {
  -webkit-appearance: none;
}
.ro-plan__cell--amt::placeholder {
  color: #c8c9cc;
}
.ro-plan__del {
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 50%;
  background: #fdeaea;
  color: #ee0a24;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.ro-addplan {
  margin-top: 12px;
  height: 40px;
  border: 1px dashed #b9cdf5;
  border-radius: 8px;
  color: #2b5fd9;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f9ff;
}

/* 文本域 */
.ro-textarea {
  margin-top: 2px;
}
.ro-textarea--alone {
  margin-top: 0;
}
.ro-textarea textarea {
  width: 100%;
  height: 74px;
  border: none;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  color: #323233;
  outline: none;
  resize: none;
  font-family: inherit;
}

/* 调试信息（默认隐藏，仅 ?debug=1 时可见） */
.ro-debug {
  padding: 8px 12px;
  font-size: 12px;
  color: #ee0a24;
  background: #fff5f5;
  border-top: 1px solid #ffd6d6;
  word-break: break-all;
}

/* 只读字段的锁标记（如「合同」：由当前合同自动带出，不允许改） */
.ro-lock {
  flex: none;
  margin-left: 6px;
  font-size: 14px;
  color: #c8c9cc;
}

/* 字段说明（如回款计划「合计需等于销售价格」） */
.ro-hint {
  width: 100%;
  margin: 2px 0 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #969799;
}

/* 底部按钮（整页表单的吸底操作条） */
.ro-actions {
  /* 不再是 sticky：作为 flex 固定项真正贴底，不随字段区滚动 */
  position: static;
  flex: none;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border-top: 1px solid #f2f3f5;
}
/* 与客户表单底部操作条一致：8px 圆角，取消=白底描边 / 保存=主色 #2b5fd9 */
.ro-btn {
  flex: 1;
  height: 44px;
  border-radius: 8px;
  border: none;
  font-size: 15px;
  font-weight: 500;
  margin: 0;
}
.ro-btn--cancel {
  background: #fff;
  border: 1px solid #dcdee0;
  color: #323233;
}
.ro-btn--save {
  background: #2b5fd9;
  color: #fff;
}
.ro-btn--save:disabled {
  opacity: 0.6;
}

/* ===== 删除确认弹窗 =====
   已全站统一到 components/ConfirmDialog.vue（居中弹窗），
   原 .cd-del-pop / .cd-del-* 自定义样式（带垃圾桶图标 + 名称信息行）已下线，勿再启用。 */

/* ===== 通用面板卡片（进度 / 合同内容 / 业务单据） ===== */
.cd-panel {
  background: #fff;
  border-radius: 12px;
  margin: 12px 12px 0;
  padding: 14px 16px 16px;
}
.cd-panel__hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.cd-sec__title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.cd-panel__link {
  font-size: 13px;
  color: #2b5fd9;
}
.cd-panel__hint {
  font-size: 12px;
  color: #a8afb8;
}

/* ===== 进度情况（stage 1待执行 2已预定 3已支付 4已全款） ===== */
.cd-progress__pct {
  font-size: 13px;
  font-weight: 600;
  color: #2b5fd9;
}
.cd-progress__bar {
  height: 8px;
  border-radius: 4px;
  background: #f1f2f4;
  overflow: hidden;
}
.cd-progress__fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(135deg, #2b5fd9 0%, #4a8af0 100%);
  transition: width .3s ease;
}
.cd-steps {
  display: flex;
  margin-top: 16px;
}
.cd-step {
  flex: 1;
  text-align: center;
  position: relative;
}
.cd-step::after {
  content: "";
  position: absolute;
  top: 11px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #eef0f3;
}
.cd-step:last-child::after {
  display: none;
}
.cd-step--on::after {
  background: #2b5fd9;
}
.cd-step__dot {
  position: relative;
  z-index: 1;
  width: 22px;
  height: 22px;
  margin: 0 auto 6px;
  border-radius: 50%;
  background: #eef0f3;
  color: #a8afb8;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cd-step--on .cd-step__dot {
  background: #2b5fd9;
  color: #fff;
}
.cd-step--cur .cd-step__dot {
  box-shadow: 0 0 0 4px rgba(43, 95, 217, .16);
}
.cd-step__label {
  font-size: 12px;
  color: #a8afb8;
}
.cd-step--on .cd-step__label {
  color: #2b5fd9;
}

/* ===== 合同字段分组（业务信息 / 会议信息 / 系统信息） ===== */
.cd-fold {
  display: flex;
  align-items: center;
  margin-top: 0;
  padding: 10px 12px;
  background: #f7f8fa;
  border-radius: 8px;
  cursor: pointer;
}
.cd-fold__title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
}
.cd-fold__hint {
  flex: 1;
  text-align: right;
  padding-right: 6px;
  font-size: 13px;
  color: #2b5fd9;
}
.cd-fold__arrow {
  color: #a8afb8;
}
.cd-fold__body {
  margin-top: 8px;
}

/* 金额字段两列宫格 */
.cd-mgrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 4px;
}
.cd-mgrid__cell {
  background: #fbfcfd;
  border: 1px solid #f0f1f3;
  border-radius: 8px;
  padding: 8px 10px;
}
.cd-mgrid__label {
  font-size: 12px;
  color: #86909c;
  margin-bottom: 4px;
}
.cd-mgrid__value {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}

/* ===== 业务单据：小图标宫格 ===== */
.cd-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 8px;
}
.cd-grid__item {
  text-align: center;
  padding: 8px 0 4px;
}
.cd-grid__icon {
  position: relative;
  width: 44px;
  height: 44px;
  margin: 0 auto 6px;
  border-radius: 12px;
  background: #eef3ff;
  color: #2b5fd9;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cd-grid__badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ee0a24;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.cd-grid__label {
  font-size: 12px;
  color: #4e5969;
}

/* ===== 单据独立页面（client 内切换，不跳路由） ===== */
.cd-billpage {
  /* 底部留白移到列表容器，避免表单模式下把底部按钮顶起来 */
  padding: 0;
}

/* ============================================================
 * 单据列表 / 详情：1:1 沿用 client.vue 主列表（客户·商机·合同）样式
 * .list-box / .crm-item / .customer-card__* / .status-tag /
 * .info-row·.info-col·.info-col1 / .btn-action
 * 不新增平行视觉语言，保证全站列表观感一致
 * ============================================================ */
.list-box {
  width: 100%;
  padding: 0 14px 24px;
  box-sizing: border-box;
  /* 与客户列表标准一致：顶栏与数据块之间留 20px，避免 sticky 顶栏切掉卡片 */
  margin-top: 20px;
}
/* 空态（模板里一直有 bl-empty，但此前无对应样式） */
.bl-empty {
  margin-top: 20px;
  padding: 64px 24px;
  text-align: center;
}
.bl-empty__icon {
  font-size: 46px;
  color: #ccd2dc;
  margin-bottom: 12px;
  line-height: 1;
}
.bl-empty__text {
  font-size: 14px;
  color: #8c8f94;
}
.bl-empty__hint {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 6px;
}
.crm-item {
  background: #fff;
  border-radius: 16px;
  margin-bottom: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(31, 58, 110, 0.07);
  /* 与客户列表一致：卡片左侧不显示蓝色竖条 */
  border-left: none;
}
.item-head {
  padding: 16px 16px 14px;
  border-bottom: 1px solid #f2f3f5;
}
.customer-card.item-head {
  border-bottom: none;
  padding: 14px 14px 12px;
}
.customer-card__main {
  display: flex;
  gap: 14px;
}
.customer-card__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.customer-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.customer-card__name-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.customer-card__name {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.customer-card__role {
  flex-shrink: 0;
  font-size: 10px;
  color: #2b5fd9;
  background: #eef2fb;
  border: 1px solid #d6e0f5;
  border-radius: 10px;
  padding: 1px 6px;
  font-weight: 500;
}
.customer-card__row {
  font-size: 12px;
  color: #4e5969;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.customer-card__row--muted {
  color: #8c8f94;
}
.customer-card__actions {
  display: flex;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f2f3f5;
}
.btn-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 0;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-action--secondary {
  color: #2b5fd9;
  background: #eef2fb;
}
.btn-action--secondary:active {
  background: #d6e0f5;
}
.btn-action--primary {
  color: #fff;
  background: linear-gradient(135deg, #2b5fd9, #4a8af0);
  box-shadow: 0 3px 10px rgba(43, 95, 217, 0.2);
}
.btn-action--primary:active {
  opacity: 0.9;
}
.btn-action--danger {
  color: #f53f3f;
  background: #ffece8;
}
.btn-action--danger:active {
  background: #ffd9d2;
}

/* 状态标签（与主列表同一套） */
.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px 2px 8px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
  flex-shrink: 0;
}
.status-tag::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.tag-danger { background: #ffece8; color: #f53f3f; }
.tag-wait { background: #fff7e8; color: #d9760a; }
.tag-allot { background: #eef2fb; color: #2b5fd9; }
.tag-receive { background: #e8ffea; color: #00a62a; }

/* ===== 整页表单：新增 / 编辑（替代原底部弹层，沿用 ro-* 表单样式） ===== */
.cd-formpage {
  display: flex;
  flex-direction: column;
  /* 底部弹窗内：撑满 popup 高度，配合 body 的 flex:1 + overflow-y:auto，
     实现「标题与底部按钮固定，只有字段区滚动」——对齐客户表单 form-page */
  height: 100%;
  background: #f5f7fb;
}
.cd-formpage__body {
  flex: 1;
  min-height: 0; /* flex 子项可收缩，否则 overflow-y 不生效 */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #f5f7fb;
  padding: 0 0 12px;
}
</style>



// WEBPACK FOOTER //
// src/pages/market/contractDetail.vue